import { SkeletonLoader, TooltipTrigger } from '@toggle/design-system';
import React from 'react';

import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';
import { CatalystInsightIcon } from '~/widgets/catalyst-insights-drawer/catalyst-insight-icon/CatalystInsightIcon';
import { EarningsInsightIcon } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-icon/EarningsInsightIcon';

import { CompanyCatalystCounterTooltip } from '../company-catalyst-counter-tooltip/CompanyCatalystCounterTooltip';
import { useInsightsCellData } from '../hooks/useInsightsCellData';
import * as S from './InsightsKGCell.styles';

const MAX_KG_INSIGHTS = 2;

export type InsightsKGCellProps = {
  entityTag: string;
  isDisabled: boolean;
  source: PosthogTrackingSource;
};

export const InsightsKGCell = ({
  entityTag,
  isDisabled,
  source,
}: InsightsKGCellProps) => {
  const {
    isLoading,
    filteredCatalystEvents = [],
    earningsInsightWithEvent,
  } = useInsightsCellData({ entityTag });

  if (isLoading) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              height: '20px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  const visibleCatalystEvents =
    filteredCatalystEvents.length >= MAX_KG_INSIGHTS
      ? filteredCatalystEvents.slice(0, MAX_KG_INSIGHTS - 1)
      : filteredCatalystEvents.slice(0, MAX_KG_INSIGHTS);

  const remainingCatalystEvents =
    filteredCatalystEvents.length >= MAX_KG_INSIGHTS
      ? filteredCatalystEvents.slice(MAX_KG_INSIGHTS - 1)
      : filteredCatalystEvents.slice(MAX_KG_INSIGHTS);

  return (
    <S.InsightsContainer>
      {earningsInsightWithEvent ? (
        <EarningsInsightIcon
          entityTag={entityTag}
          earningsInsight={earningsInsightWithEvent.earningsInsight}
          eventId={earningsInsightWithEvent.eventId}
          read={earningsInsightWithEvent.read}
          width={18}
          height={20}
          source={source}
          isDisabled={isDisabled}
        />
      ) : (
        <S.Placeholder />
      )}
      {visibleCatalystEvents.map(
        event =>
          event.metadata.category && (
            <CatalystInsightIcon
              key={event.source_id}
              catalystInsightId={event.source_id}
              subCategory={event.metadata.sub_category ?? undefined}
              isBordered
              customSize={{ width: 18, height: 20 }}
              identifier={entityTag}
              isDisabled={isDisabled}
              source={source}
              category={event.metadata.category}
              eventId={event.id}
              read={event.read}
            />
          )
      )}
      {!!remainingCatalystEvents.length && (
        <TooltipContainer
          label={
            <CompanyCatalystCounterTooltip
              events={remainingCatalystEvents}
              source={source}
            />
          }
          inPortal
          trigger={[TooltipTrigger.Hover]}
          placement="top-end"
          withSafePolygon
          disabled={isDisabled}
        >
          <S.Count $isDisabled={isDisabled}>
            +{remainingCatalystEvents.length}
          </S.Count>
        </TooltipContainer>
      )}
    </S.InsightsContainer>
  );
};
